import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  Coins, 
  TrendingUp, 
  Users, 
  FileCheck, 
  ArrowUpRight, 
  ArrowDownRight,
  Plus,
  BarChart3,
  Shield,
  Clock
} from 'lucide-react';
import { Card, Badge, Button } from '../../components/ui';
import { api } from '../../lib/api';

interface DashboardStats {
  totalAssets: number;
  activeAssets: number;
  pendingApproval: number;
  totalTokensIssued: number;
  totalValueTokenized: number;
  totalInvestors: number;
  totalYieldDistributed: number;
  porCoverage: number;
}

interface RecentActivity {
  id: string;
  type: 'MINT' | 'BURN' | 'TRADE' | 'YIELD' | 'APPROVAL';
  description: string;
  amount?: number;
  createdAt: string;
}

interface AssetSummary {
  id: string;
  name: string;
  symbol: string;
  category: string;
  status: string;
  totalSupply: number;
  circulatingSupply: number;
  porCoverage: number;
}

export function TenantTokenizationDashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: ['tokenization-dashboard-stats'],
    queryFn: async () => {
      const res = await api.get('/tokenization/dashboard/tenant/stats');
      return res.data;
    }
  });

  const { data: recentActivity } = useQuery<RecentActivity[]>({
    queryKey: ['tokenization-recent-activity'],
    queryFn: async () => {
      const res = await api.get('/tokenization/dashboard/tenant/activity');
      return res.data;
    }
  });

  const { data: assets } = useQuery<AssetSummary[]>({
    queryKey: ['tokenization-assets-summary'],
    queryFn: async () => {
      const res = await api.get('/tokenization/dashboard/tenant/assets');
      return res.data;
    }
  });

  const formatCurrency = (value: number) => 
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

  const formatNumber = (value: number) => 
    new Intl.NumberFormat('pt-BR').format(value);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'MINT': return <ArrowUpRight className="w-4 h-4 text-green-400" />;
      case 'BURN': return <ArrowDownRight className="w-4 h-4 text-red-400" />;
      case 'TRADE': return <BarChart3 className="w-4 h-4 text-blue-400" />;
      case 'YIELD': return <Coins className="w-4 h-4 text-yellow-400" />;
      case 'APPROVAL': return <FileCheck className="w-4 h-4 text-purple-400" />;
      default: return <Clock className="w-4 h-4 text-surface-400" />;
    }
  };

  if (statsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-surface-800 rounded w-64" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-surface-800 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Dashboard de Tokenização</h1>
          <p className="text-surface-400">Visão geral dos seus ativos tokenizados</p>
        </div>
        <Link to="/admin/tokenization/assets/new">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Novo Ativo
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-surface-400 text-sm">Ativos Tokenizados</p>
              <p className="text-2xl font-bold text-surface-100 mt-1">
                {stats?.activeAssets || 0}
              </p>
              <p className="text-xs text-surface-500 mt-1">
                {stats?.pendingApproval || 0} pendentes
              </p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <Coins className="w-6 h-6 text-primary-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-surface-400 text-sm">Valor Tokenizado</p>
              <p className="text-2xl font-bold text-surface-100 mt-1">
                {formatCurrency(stats?.totalValueTokenized || 0)}
              </p>
              <p className="text-xs text-surface-500 mt-1">
                {formatNumber(stats?.totalTokensIssued || 0)} tokens
              </p>
            </div>
            <div className="p-3 bg-green-500/20 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-surface-400 text-sm">Investidores</p>
              <p className="text-2xl font-bold text-surface-100 mt-1">
                {formatNumber(stats?.totalInvestors || 0)}
              </p>
              <p className="text-xs text-surface-500 mt-1">
                Holders únicos
              </p>
            </div>
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <Users className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-surface-400 text-sm">Cobertura PoR</p>
              <p className="text-2xl font-bold text-surface-100 mt-1">
                {(stats?.porCoverage || 0).toFixed(1)}%
              </p>
              <p className="text-xs text-surface-500 mt-1">
                Proof of Reserve
              </p>
            </div>
            <div className={`p-3 rounded-lg ${
              (stats?.porCoverage || 0) >= 100 ? 'bg-green-500/20' : 'bg-yellow-500/20'
            }`}>
              <Shield className={`w-6 h-6 ${
                (stats?.porCoverage || 0) >= 100 ? 'text-green-400' : 'text-yellow-400'
              }`} />
            </div>
          </div>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Assets List */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-surface-100">Meus Ativos</h3>
              <Link to="/admin/tokenization/assets" className="text-sm text-primary-400 hover:text-primary-300">
                Ver todos
              </Link>
            </div>
            
            <div className="space-y-3">
              {assets?.slice(0, 5).map(asset => (
                <Link 
                  key={asset.id} 
                  to={`/admin/tokenization/assets/${asset.id}`}
                  className="flex items-center justify-between p-4 bg-surface-800/50 rounded-lg hover:bg-surface-800 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center">
                      <Coins className="w-5 h-5 text-primary-400" />
                    </div>
                    <div>
                      <p className="font-medium text-surface-100">{asset.name}</p>
                      <p className="text-sm text-surface-400">{asset.symbol}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-surface-100">{formatNumber(asset.circulatingSupply)} tokens</p>
                    <div className="flex items-center gap-2 justify-end mt-1">
                      <Badge variant={asset.status === 'ACTIVE' ? 'success' : 'warning'} className="text-xs">
                        {asset.status === 'ACTIVE' ? 'Ativo' : 'Pendente'}
                      </Badge>
                      {asset.porCoverage >= 100 && (
                        <Badge variant="success" className="text-xs">
                          PoR ✓
                        </Badge>
                      )}
                    </div>
                  </div>
                </Link>
              ))}

              {(!assets || assets.length === 0) && (
                <div className="text-center py-8">
                  <Coins className="w-12 h-12 text-surface-600 mx-auto mb-3" />
                  <p className="text-surface-400">Nenhum ativo tokenizado ainda</p>
                  <Link to="/admin/tokenization/assets/new">
                    <Button variant="secondary" className="mt-4">
                      <Plus className="w-4 h-4 mr-2" />
                      Criar Primeiro Ativo
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Recent Activity */}
        <div>
          <Card className="p-6">
            <h3 className="font-semibold text-surface-100 mb-4">Atividade Recente</h3>
            
            <div className="space-y-3">
              {recentActivity?.slice(0, 8).map(activity => (
                <div key={activity.id} className="flex items-start gap-3 p-3 bg-surface-800/30 rounded-lg">
                  <div className="mt-0.5">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-surface-100 truncate">{activity.description}</p>
                    {activity.amount && (
                      <p className="text-xs text-surface-400">{formatCurrency(activity.amount)}</p>
                    )}
                    <p className="text-xs text-surface-500 mt-1">
                      {new Date(activity.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              ))}

              {(!recentActivity || recentActivity.length === 0) && (
                <div className="text-center py-6">
                  <Clock className="w-8 h-8 text-surface-600 mx-auto mb-2" />
                  <p className="text-sm text-surface-400">Nenhuma atividade recente</p>
                </div>
              )}
            </div>
          </Card>

          {/* Yield Summary */}
          <Card className="p-6 mt-6">
            <h3 className="font-semibold text-surface-100 mb-4">Rendimentos Distribuídos</h3>
            <div className="text-center py-4">
              <p className="text-3xl font-bold text-green-400">
                {formatCurrency(stats?.totalYieldDistributed || 0)}
              </p>
              <p className="text-sm text-surface-400 mt-1">Total distribuído</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default TenantTokenizationDashboard;
